import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { loginOptions } from '../auth/login-options';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { NotificationSettingsDto, ParkerService, SignupRequestRegisteredParker } from '../../api/gen';
import Keycloak from 'keycloak-js';
import { JjamesButtonComponent } from 'shared-lib';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    JjamesButtonComponent
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent implements OnInit {
  registerForm!: FormGroup;
  isLoading = signal(false);
  showPassword = signal(false);
  showConfirmPassword = signal(false);
  formErrorMessage = signal<string | null>(null);
  isEmailAlreadyRegistered = signal<boolean>(false);

  private readonly keycloak = inject(Keycloak);
  private readonly parkerService = inject(ParkerService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8), this.passwordValidator]],
      confirmPassword: ['', [Validators.required]],
      enableMarketingNotifications: [false],
      agreeToTerms: [false, [Validators.requiredTrue]]
    }, { validators: this.passwordMatchValidator });

    // Clear server error banner when user changes the email
    this.registerForm.get('email')?.valueChanges.subscribe(() => {
      if (this.isEmailAlreadyRegistered()) {
        this.isEmailAlreadyRegistered.set(false);
        this.formErrorMessage.set(null);
        const errors = this.registerForm.get('email')?.errors;
        if (errors && errors['alreadyRegistered']) {
          delete errors['alreadyRegistered'];
          const remainingErrors = Object.keys(errors).length ? errors : null;
          this.registerForm.get('email')?.setErrors(remainingErrors);
        }
      }
    });
  }

  private passwordValidator(control: any) {
    const value = control.value;
    if (!value) return null;

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumeric = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    const valid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar;
    return valid ? null : {
      passwordStrength: {
        hasUpperCase,
        hasLowerCase,
        hasNumeric,
        hasSpecialChar
      }
    };
  }

  private passwordMatchValidator(group: FormGroup) {
    const password = group.get('password');
    const confirmPassword = group.get('confirmPassword');

    if (!password || !confirmPassword) return null;

    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  get passwordValue(): string {
    return this.registerForm.get('password')?.value || '';
  }

  get hasMinLength(): boolean {
    return this.passwordValue.length >= 8;
  }

  get hasUpperAndLower(): boolean {
    return /[A-Z]/.test(this.passwordValue) && /[a-z]/.test(this.passwordValue);
  }

  get hasNumeric(): boolean {
    return /[0-9]/.test(this.passwordValue);
  }

  get hasSpecialChar(): boolean {
    return /[!@#$%^&*(),.?":{}|<>]/.test(this.passwordValue);
  }

  onSubmit(): void {
    this.formErrorMessage.set(null);
    this.isEmailAlreadyRegistered.set(false);

    if (this.registerForm.valid) {
      this.isLoading.set(true);

      const formValue = this.registerForm.value;
      const notificationSettings: NotificationSettingsDto = {
        enableMarketingNotifications: formValue.enableMarketingNotifications
      };

      const signupRequest: SignupRequestRegisteredParker = {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        email: formValue.email,
        password: formValue.password,
        notificationSettings
      };

      this.parkerService.parkerSignupPost(signupRequest).subscribe({
        next: (result) => {
          this.isLoading.set(false);
          this.snackBar.open(
            'Account created successfully! Please sign in.',
            'Close',
            { duration: 5000, panelClass: ['success-snackbar'] }
          );
          this.navigateToLogin();
        },
        error: (error) => {
          this.isLoading.set(false);
          console.error('Registration error:', error);
          const detailedMessage = this.extractErrorMessage(error);
          this.handleRegistrationError(detailedMessage);
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  private extractErrorMessage(error: any): string {
    if (!error) return 'Registration failed. Please check your information and try again.';

    // Check if error.error is a direct string from the backend (e.g. "Email is already registered.")
    if (typeof error.error === 'string') {
      const trimmed = error.error.trim();
      if (trimmed) return trimmed;
    }

    // Check if error.error is an object
    if (error.error && typeof error.error === 'object') {
      if (error.error.detail && typeof error.error.detail === 'string') {
        return error.error.detail;
      }
      if (error.error.message && typeof error.error.message === 'string') {
        return error.error.message;
      }
      if (error.error.description && typeof error.error.description === 'string') {
        return error.error.description;
      }
      if (error.error.title && typeof error.error.title === 'string' && error.error.title !== 'Bad Request' && error.error.title !== 'One or more validation errors occurred.') {
        return error.error.title;
      }
      if (error.error.errors && typeof error.error.errors === 'object') {
        const messages: string[] = [];
        for (const key of Object.keys(error.error.errors)) {
          const val = error.error.errors[key];
          if (Array.isArray(val)) {
            messages.push(...val);
          } else if (typeof val === 'string') {
            messages.push(val);
          }
        }
        if (messages.length > 0) {
          return messages.join('. ');
        }
      }
    }

    if (error.status === 409) {
      return 'Email is already registered.';
    }

    if (error.message && typeof error.message === 'string' && !error.message.startsWith('Http failure response')) {
      return error.message;
    }

    return 'Registration failed. Please check your information and try again.';
  }

  private handleRegistrationError(message: string): void {
    const isEmailTaken = message.toLowerCase().includes('already registered') ||
                         message.toLowerCase().includes('already exists') ||
                         message.toLowerCase().includes('duplicate email');

    if (isEmailTaken) {
      this.isEmailAlreadyRegistered.set(true);
      const emailCtrl = this.registerForm.get('email');
      emailCtrl?.setErrors({ ...emailCtrl.errors, alreadyRegistered: true });
      emailCtrl?.markAsTouched();
      this.formErrorMessage.set('This email address is already registered. If you already have an account, please sign in.');
    } else {
      this.formErrorMessage.set(message);
    }

    this.snackBar.open(
      message,
      'Close',
      { duration: 8000, panelClass: ['error-snackbar'] }
    );
  }

  private markFormGroupTouched(): void {
    Object.keys(this.registerForm.controls).forEach(key => {
      const control = this.registerForm.get(key);
      control?.markAsTouched();
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword.set(!this.showPassword());
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.set(!this.showConfirmPassword());
  }

  getFieldError(fieldName: string): string {
    const field = this.registerForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['alreadyRegistered']) return 'This email address is already registered';
      if (field.errors['required']) return `${this.getFieldDisplayName(fieldName)} is required`;
      if (field.errors['email']) return 'Please enter a valid email address';
      if (field.errors['minlength']) return `${this.getFieldDisplayName(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['passwordStrength']) return 'Please meet all password requirements below';
      if (fieldName === 'confirmPassword' && this.registerForm.errors?.['passwordMismatch']) return 'Passwords do not match';
      if (field.errors['required'] && fieldName === 'agreeToTerms') return 'You must agree to the terms and privacy policy';
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const names: { [key: string]: string } = {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email address',
      password: 'Password',
      confirmPassword: 'Confirm password'
    };
    return names[fieldName] || fieldName;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.registerForm.get(fieldName);
    return !!(field?.invalid && field?.touched);
  }

  navigateToLogin(): void {
    this.keycloak.login(loginOptions());
  }
}
